"use client";

import { useParams, useApiReady } from "@/hooks";
import { AppDispatch } from "@/store";
import { clearDashboardFinancial } from "@/store/modules/dashboardFinancial/slice";
import { fetchDashboardFinancial } from "@/store/modules/dashboardFinancial/thunks";
import { UseDataRangeFilter } from "@/types";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const useDashboardFinancial = ({
  dateRange,
  onSuccess,
}: UseDataRangeFilter) => {
  const { params } = useParams();
  const { isReady: isApiReady } = useApiReady();

  const { dashboardFinancial, loading, error } = useSelector(
    (state: any) => state.dashboardFinancial,
  );
  const dispatch: AppDispatch = useDispatch();

  // Use the exact selected dates (YYYY-MM-DD) to match behavior of useDashboard
  const isValidDate = (d: unknown): d is Date =>
    d instanceof Date && !isNaN(d.getTime());

  const formatDateToString = (d: Date | undefined | null) => {
    if (!d || !isValidDate(d)) return null;
    return d.toISOString().split("T")[0];
  };

  const startDateStr = formatDateToString(dateRange?.start);
  const endDateStr = formatDateToString(dateRange?.end);

  // Estabilizar params usando JSON.stringify
  const paramsStringified = useMemo(() => JSON.stringify(params), [params]);

  // Busca automática inicial - aguarda API estar pronta
  useEffect(() => {
    if (!isApiReady) return;
    if (!startDateStr || !endDateStr) return;

    const payload = {
      ...params,
      start_date: startDateStr,
      end_date: endDateStr,
    };
    dispatch(fetchDashboardFinancial(payload));
  }, [isApiReady, paramsStringified, startDateStr, endDateStr, dispatch]);

  const handleSubmitFilter = async () => {
    const payload: Record<string, any> = {
      ...params,
      start_date: startDateStr,
      end_date: endDateStr,
    };

    try {
      await dispatch(fetchDashboardFinancial(payload)).unwrap();
    } catch (err: any) {
      if (err?.errors) {
        Object.values(err.errors)
          .flat()
          .forEach((msg: any) => {
            toast.error(msg);
          });
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Erro ao buscar dados financeiros.");
      }

      dispatch(clearDashboardFinancial());
    }
  };

  return {
    dashboardFinancial,
    loading,
    error,
    handleSubmitFilter,
  };
};

export default useDashboardFinancial;
